import {
  updateActiveTenancyId,
  openLeadFormModal,
} from "@Components/LeadFormModal/data/leadFormReducer";
import handleLeadAPICall from "@Components/LeadFormV2/utils/handleLeadAPICall";
import { AppState } from "@Store/index";
import useIsHappyFlow from "hooks/useIsHappyFlow";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { getNewLeadFormPageRedirectURL } from "../../../utils";
import useIsDesktop from "hooks/useIsDesktop";
import getParsedLocalStorageData from "@Components/LeadFormModal/helpers/getParsedLocalStorageData";
import allFieldsFilled from "helpers/getIsAllFieldsFilled";
import { useHistory } from "hooks/useHistory";
import { getRedirectURL } from "@Components/LeadForm/utils";
import viewTransitionNavUtility from "@Utils/viewTransitionNavUtility";
import useIsHappyFlowBookForm from "hooks/useIsHappyFlowBookForm";
import getPaymentFormLink from "@Pages/PropertyPage/components/PropertyCTAs/utils/getPaymentFormLink";
import isPaymentActive from "@Utils/isPaymentActive";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import config from "@Config/index";
import useWhitelabel from "hooks/useWhitelabel";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import { logAppEvent } from "@Utils/appAnalytics";
import { APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import { isWhitelabelPartnerInventoryFunc } from "@Utils/whitelabelHelper";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";

const useBookClick = (data: any, customWindowOpen?: any) => {
  const {
    personalDetails,
    isLeadFormFilled,
    inModal,
    region,
    isLFOneFilled,
    accommodationDetails,
    filters,
    propertyId,
    propertyData,
    rooms,
  }: any = useSelector(
    ({ leadFormModal, searchPageData, propertyPageData }: AppState) => ({
      personalDetails: leadFormModal.personalDetails,
      isLeadFormFilled: leadFormModal.isLeadFormFilled,
      inModal: leadFormModal.inModal,
      region: searchPageData.region,
      isLFOneFilled: leadFormModal.isLFOneFilled,
      accommodationDetails: leadFormModal.accommodationDetails,
      filters: propertyPageData.filters,
      propertyId: propertyPageData.id,
      propertyData: propertyPageData.data,
      rooms: propertyPageData.roomTypes.data,
    }),
    shallowEqual,
  );
  const { isWhitelabel, isDemoSubdomain, partner_uuid, partner_id } = useWhitelabel();
  const isHappyFlow = useIsHappyFlow();
  const isWhitelabelPartnerInventory = isWhitelabelPartnerInventoryFunc(propertyData, partner_id);
  const openingSourceMap: Record<string, string> = {
    "Contact Us": "contact_us",
    "Book Now": "book_now",
    "Apply Now": "apply_now",
    "Join Waitlist": "join_waitlist",
    "Apply For Fall": "apply_for_fall",
  };
  const isDesktop = useIsDesktop();
  const dispatch = useDispatch();
  const history = useHistory();
  const { isBelow } = useResponsiveness();
  const isMobile = isBelow(SCREEN_SIZE.SM);
  const isHappyFlowBookForm = useIsHappyFlowBookForm();
  const isPaymentBeforeBookform =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.ENABLE_PAYMENT_BEFORE_BF) &&
    propertyData?.payment_before_bookform &&
    isPaymentActive(propertyData);
  const openLeadModalFromBookCTA = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.OPEN_LEAD_MODAL_FROM_BOOK_CTA,
  );
  const onBookClick = (
    tenancy: {
      id?: number | string;
      config?: {
        id: number | string;
      };
    } = {},
    heading: string,
    fromBookNow = false,
    roomId = 0,
  ) => {
    logAppEvent({
      eventName: APP_EVENTS.LEAD_CTA_CLICKED,
      payload: {
        cta_name: openingSourceMap[heading] || "",
        screen_name: "",
        section_name: "",
        lead_flow: "",
      },
    });

    const inventory = {
      ...tenancy,
      tenancyName: data.name,
      name: propertyData?.name,
      location_coordinates: propertyData?.location_coordinates,
      location: propertyData?.location,
      canonical_name: propertyData?.canonical_name,
      parent: propertyData,
    };
    const leadFormRedirectURL = getNewLeadFormPageRedirectURL(inventory);
    const leadDetails = getParsedLocalStorageData("lead-form-one-saved-data");
    const redirectToLeadPage = isHappyFlow
      ? !isLFOneFilled && fromBookNow && !isDesktop && !allFieldsFilled(leadDetails)
      : fromBookNow && !isDesktop && !allFieldsFilled(leadDetails);

    if (isHappyFlow && fromBookNow && isPaymentBeforeBookform && !isWhitelabelPartnerInventory) {
      const link = getPaymentFormLink({
        rooms,
        // @ts-ignore
        id: tenancy?.id || tenancy?.config?.id,
        canonicalName: propertyData.canonical_name,
        isPaymentBeforeBookform,
        source: "enquire_property",
      });
      const redirectionURL = isWhitelabel
        ? convertToWhitelabelURL(link, isDemoSubdomain, partner_uuid)
        : link;
      viewTransitionNavUtility(() => history.push(redirectionURL), "right-to-left");
    } else if (config.IS_CHINA_APP && fromBookNow && isLFOneFilled) {
      const redirectionURL = getRedirectURL({
        inventory,
        inModal: true,
        postSuccessData: {},
        formOneData: {},
        formTwoSavedData: {},
        withRedirectAnimation: false,
        bookFormSource:
          openingSourceMap[heading] === "contact_us" ? "book_tenancy" : "enquire_property",
        showSubmitConfirmation: true,
      });
      viewTransitionNavUtility(() => history.push(redirectionURL), "right-to-left");
    } else if (
      fromBookNow &&
      isHappyFlowBookForm &&
      (openLeadModalFromBookCTA ? isLFOneFilled : true) &&
      !isWhitelabelPartnerInventory
    ) {
      let redirectionURL = getRedirectURL({
        inventory,
        inModal: true,
        postSuccessData: {},
        formOneData: {},
        formTwoSavedData: {},
        withRedirectAnimation: false,
        bookFormSource:
          openingSourceMap[heading] === "contact_us" ? "book_tenancy" : "enquire_property",
        showSubmitConfirmation: true,
      });
      redirectionURL = isWhitelabel
        ? convertToWhitelabelURL(redirectionURL, isDemoSubdomain, partner_uuid)
        : redirectionURL;
      viewTransitionNavUtility(() => history.push(redirectionURL), "right-to-left");
    } else if (redirectToLeadPage && !openLeadModalFromBookCTA) {
      if (customWindowOpen) {
        customWindowOpen({
          url: isWhitelabel
            ? convertToWhitelabelURL(leadFormRedirectURL, isDemoSubdomain, partner_uuid)
            : leadFormRedirectURL,
          target: "_self",
        });
      }
    } else {
      (async () => {
        let updatedLFOneFilled = isLFOneFilled;
        // check for contact us flow
        if (
          Object.keys(accommodationDetails).length <= 0 &&
          isLFOneFilled &&
          heading === "Contact Us"
        ) {
          updatedLFOneFilled = false;
        }

        if (isHappyFlow && updatedLFOneFilled && !fromBookNow) {
          // @ts-ignore
          dispatch(updateActiveTenancyId({ activeTenancyId: inventory?.id ?? roomId }));
          await handleLeadAPICall({
            dispatch,
            isLFOneFilled,
            region,
            inventory,
            inModal,
            openingSource: openingSourceMap[heading] || "",
            isLeadFormFilled,
            formData: personalDetails,
            filters,
            propertyId,
            isDesktop,
            history,
            isPaymentBeforeBookform,
            redirectWithoutSuccessScreen: openLeadModalFromBookCTA,
            isWhitelabel,
            isDemoSubdomain,
            partner_uuid,
            roomId,
            tenancyId: tenancy?.id || tenancy?.config?.id,
            isMobile,
          });
          dispatch(updateActiveTenancyId({ activeTenancyId: 0 }));
        } else
          dispatch(
            openLeadFormModal({
              leadFormModalHeading: heading,
              inventory,
              inModal: true,
              openingSource: openingSourceMap[heading] || "",
              leadFormPage: 1,
            }),
          );
      })();
    }
  };
  return { onBookClick };
};

export default useBookClick;
